import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuidv4 } from 'uuid';

export interface LoggerConfig {
  applicationId: string;
  clientToken: string;
  site?: string;
  service?: string;
  env?: string;
  version?: string;
  sampleRate?: number;
  trackInteractions?: boolean;
  allowedTracingUrls?: (string | RegExp)[];
  /** Initial consent state. When false (the default), RUM sets no cookies and
   * collects nothing until setTrackingConsent(true) is called. */
  trackingConsent?: boolean;
  /** Session Replay is on by default. Pass false on a page whose URL carries a
   * credential: replay records the location itself and beforeSend cannot redact it. */
  sessionReplay?: boolean;
}

/**
 * Whether a pathname is a customer-portal click-to-pay path, whose next segment is a
 * live account credential.
 *
 * Decoded and case-insensitively matched because that is how the portal's router
 * matches: `/%70/CODE` and `/P/CODE` are working links that a raw needle would miss.
 */
export function carriesPortalCredential(pathname: string): boolean {
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    // Malformed escape — no route matches it either, so judge the raw path.
  }
  return /^\/+p(\/|$)/i.test(decoded);
}

/**
 * Strips the credential out of customer-portal click-to-pay URLs.
 *
 * RUM records the full URL on every view, resource and error, so it has to be redacted
 * before the event leaves the browser. Returns undefined when there is nothing to
 * redact, so callers leave the original value alone.
 */
function redactUrl(url: unknown): string | undefined {
  if (typeof url !== 'string') return undefined;

  try {
    const parsed = new URL(url);
    return carriesPortalCredential(parsed.pathname) ? `${parsed.origin}/p/{code}${parsed.search}${parsed.hash}` : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Strips click-to-pay credentials out of free text — an error message, a stack frame, a
 * script filename. A script that throws while the page is a click-to-pay URL names that
 * URL in all three, so redacting only structured URL fields would still ship the code.
 */
export function redactCredentialsInText(text: string): string {
  // Path characters only, so a stack frame keeps the `:line:col` that follows its URL.
  return text.replace(/\/(?:p|%70|%50)\/[A-Za-z0-9%_-]*(?:\/[A-Za-z0-9%_-]*)*/gi, '/p/{code}');
}

export class Logger {
  private static instance: Logger;
  private traceId: string;
  private initialized = false;
  private errorHandler: ((event: ErrorEvent) => void) | null = null;
  private rejectionHandler: ((event: PromiseRejectionEvent) => void) | null = null;

  private constructor() {
    // Generate a trace ID that persists for the session
    // This ensures consistent trace ID propagation across requests
    this.traceId = uuidv4();
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  public init(config: LoggerConfig): void {
    if (this.initialized) {
      return;
    }

    datadogRum.init({
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site || 'datadoghq.eu',
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sampleRate || 100,
      sessionReplaySampleRate: 20,
      // Without this the recorder starts inside init(), so the call below would decide
      // nothing — and a browser already carrying a sampled-in session records the URL
      // whatever sample rate we pass.
      startSessionReplayRecordingManually: true,
      trackUserInteractions: config.trackInteractions ?? true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask',
      allowedTracingUrls: config.allowedTracingUrls,
      trackingConsent: config.trackingConsent ? 'granted' : 'not-granted',
      beforeSend: (event) => {
        const url = redactUrl(event.view.url);
        if (url) event.view.url = url;

        const referrer = redactUrl(event.view.referrer);
        if (referrer) event.view.referrer = referrer;

        if (event.type === 'resource') {
          const resourceUrl = redactUrl(event.resource.url);
          if (resourceUrl) event.resource.url = resourceUrl;
        }

        if (event.type === 'error') {
          if (event.error.resource) {
            const errorResourceUrl = redactUrl(event.error.resource.url);
            if (errorResourceUrl) event.error.resource.url = errorResourceUrl;
          }
          event.error.message = redactCredentialsInText(event.error.message);
          if (event.error.stack) event.error.stack = redactCredentialsInText(event.error.stack);
        }

        return true;
      },
    });

    if (config.sessionReplay !== false) {
      datadogRum.startSessionReplayRecording();
    }

    // Set up global error handlers
    this.setupGlobalErrorHandlers();

    this.initialized = true;
  }

  /**
   * Update the visitor's RUM tracking consent after init. Granting starts
   * collection; revoking stops it and clears RUM's stored data — no page
   * reload needed. No-op until init() has run.
   */
  public setTrackingConsent(granted: boolean): void {
    if (!this.initialized) {
      return;
    }
    datadogRum.setTrackingConsent(granted ? 'granted' : 'not-granted');
  }

  /**
   * Set up global error handlers to capture unhandled errors and promise rejections.
   * These are automatically reported to Datadog RUM for Error Tracking.
   */
  private setupGlobalErrorHandlers(): void {
    // Only set up handlers in browser environment
    if (typeof window === 'undefined') {
      return;
    }

    // Store handlers to allow cleanup
    // A script throwing while the page is a click-to-pay URL names that URL as its
    // filename, so the context carries the credential unless it is scrubbed here.
    this.errorHandler = (event: ErrorEvent) => {
      this.error('Unhandled error', event.error, {
        message: redactCredentialsInText(event.message),
        filename: redactCredentialsInText(event.filename),
        lineno: event.lineno,
        colno: event.colno,
        traceId: this.traceId,
      });
    };

    this.rejectionHandler = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      this.error('Unhandled promise rejection', error, {
        reason: redactCredentialsInText(String(event.reason)),
        traceId: this.traceId,
      });
    };

    window.addEventListener('error', this.errorHandler);
    window.addEventListener('unhandledrejection', this.rejectionHandler);
  }

  /**
   * Clean up global error handlers.
   * Call this when the logger is no longer needed to prevent memory leaks.
   */
  public cleanup(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler);
      this.errorHandler = null;
    }
    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler);
      this.rejectionHandler = null;
    }
  }

  /**
   * Get headers for trace propagation.
   * Returns X-Datadog-Trace-Id header with a consistent trace ID for the session.
   * This ensures trace ID propagation when using custom fetch wrappers.
   */
  public getHeaders(): Record<string, string> {
    return {
      'X-Datadog-Trace-Id': this.traceId,
    };
  }

  /**
   * Get the current trace ID for correlation.
   */
  public getTraceId(): string {
    return this.traceId;
  }

  public log(message: string, context?: object): void {
    console.log(message, context);
    datadogRum.addAction(message, context);
  }

  public error(message: string, error?: Error, context?: object): void {
    console.error(message, error, context);
    datadogRum.addError(error || new Error(message), {
      ...context,
      traceId: this.traceId,
      timestamp: new Date().toISOString(),
    });
  }
}

export const logger = Logger.getInstance();
